import { motion } from "framer-motion";
import { Server, Network, Code2, Cloud } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { spotlightTilt } from "@/components/site/fx";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const MEMBERS = [
  { initials: "IN", icon: Server, k: "m1", tags: ["Proxmox", "ZFS", "Linux", "Windows Server"] },
  { initials: "SEC", icon: Network, k: "m2", tags: ["pfSense", "MikroTik", "VPN", "Firewall"] },
  { initials: "DEV", icon: Code2, k: "m3", tags: ["Web Apps", "API", "ERP", "Integrări"] },
  { initials: "OPS", icon: Cloud, k: "m4", tags: ["Docker", "Coolify", "Cloudflare", "CI/CD"] },
];

const MemberCard = ({ m, i }) => {
  const Icon = m.icon;
  const { t } = useI18n();
  return (
    <motion.div
      data-testid={`team-member-${i}`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: EASE, delay: (i % 4) * 0.08 }}
    >
      <article
        {...spotlightTilt(6)}
        style={{ willChange: "transform" }}
        className="spotlight card-edge group relative flex h-full flex-col overflow-hidden rounded-2xl p-7"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-neon/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />

        <div className="relative flex items-start justify-between">
          <span className="grid h-16 w-16 place-items-center rounded-xl border border-white/10 bg-ink-900 font-display text-lg font-extrabold tracking-tight text-cyan-neon transition-colors duration-300 group-hover:border-cyan-neon/40">
            {m.initials}
          </span>
          <span className="grid h-10 w-10 place-items-center border border-white/10 text-white/40 transition-colors duration-300 group-hover:text-cyan-neon">
            <Icon size={20} strokeWidth={1.6} />
          </span>
        </div>

        <div className="relative mt-6 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">{t("tm.available")}</span>
          </div>
          <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-white">
            {t(`tm.${m.k}.role`)}
          </h3>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-white/40">
            {t(`tm.${m.k}.title`)}
          </div>
          <p className="mt-4 font-body text-sm leading-relaxed text-white/55">
            {t(`tm.${m.k}.bio`)}
          </p>
        </div>

        <div className="relative mt-5 flex flex-wrap gap-2">
          {m.tags.map((t) => (
            <span
              key={t}
              className="border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/45"
            >
              {t}
            </span>
          ))}
        </div>
      </article>
    </motion.div>
  );
};

export const Team = () => {
  const { t } = useI18n();
  return (
    <section
      id="echipa"
      data-testid="team-section"
      className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32"
    >
      <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div className="max-w-2xl">
          <Reveal>
            <SectionLabel index="[ • ]">{t("tm.label")}</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("tm.h1")} <span className="text-aurora">{t("tm.hkw")}</span> {t("tm.h2")}
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="max-w-xs font-body text-sm leading-relaxed text-white/50">
            {t("tm.sub")}
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {MEMBERS.map((m, i) => (
          <MemberCard key={m.k} m={m} i={i} />
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-4">
          {[
            { v: "20+", l: t("tm.st.years") },
            { v: "24/7", l: t("tm.st.avail") },
            { v: "4", l: t("tm.st.areas") },
            { v: "100%", l: t("tm.st.prod") },
          ].map((s) => (
            <div key={s.l} className="bg-ink-900/70 px-5 py-6 text-center">
              <div className="font-display text-2xl font-extrabold text-cyan-neon sm:text-3xl">{s.v}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/45">{s.l}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};
